import React, { createContext, useEffect, useState } from "react";
import { Preferences } from "@capacitor/preferences";
import axios from "axios";
import {
  isOrgDept,
  isProject,
  isTicketDept,
} from "../utils/departmentTypes";
import { assignableRolesFor } from "../utils/roles";
import { ALL_BRANCHES } from "../utils/branchDisplay";

export const AutoTaskAuthContext = createContext();

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const STORAGE_KEYS = {
  USER: "user",
  ROLE: "role",
  GOALS: "goals",
  TEMPLATES: "templates_data",
  COMPANY_SETTINGS: "company_settings",
  DEFAULT_FY: "default_fy",
  DEFAULT_FY_ID: "default_fy_id",
  // Also mirrored to a raw localStorage key of the same name, because that
  // is what atsModule/services/api.js reads. See saveLoginData.
  TOKEN: "token",
  // The branch the session is currently in, as its own cached object.
  CURRENT_BRANCH: "current_branch",
};

// Written by an older build: { [store_id]: branch_id }, the branch this device
// last used in each store. Nothing reads or writes it any more — see "Which
// branch a session starts in" below — and logout clears it so the ones already
// sitting on people's devices go away.
const LEGACY_LAST_BRANCH_KEY = "last_branch_by_store";

// ─────────────────────────────────────────────────────────────────────────────
// Which branch a session starts in
//
// The account decides, and only the account: a pinned role opens in their own
// branch, everybody else in the store's MAIN branch. The client sends no
// preference at all — logging in and switching store both leave branch_id out
// of the call, and branchHelpers.resolveSessionBranch falls through to
// pinned-or-main on its own.
//
// An earlier build remembered the branch each device last used per store and
// sent it back, so a head who worked out of a third branch landed there every
// morning. It cost more than it saved. The login picker and the store switcher
// both label a row with the branch the SERVER would pick, and the server
// cannot know what one browser remembers — so the chip read "BTM Branch" while
// the session opened in Kamakhya. Rather than teach every label to consult a
// local map, entering a store is now always the same place, and BranchSwitcher
// moves you off it for as long as that session lasts.
// ─────────────────────────────────────────────────────────────────────────────

const getCurrentFY = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  return month >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};

export const AutoTaskContext = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [userGoals, setUserGoals] = useState([]);
  const [role, setRole] = useState(null);

  const [templateData, setTemplateData] = useState({
    token: null,
    templates: [],
  });

  const [companySettings, setCompanySettings] = useState({
    bank_details: {
      bank_name: null,
      account_holder_name: null,
      account_number: null,
      ifsc_code: null,
      branch_name: null,
      upi_id: null,
    },
    invoice: {
      invoice_prefix: null,
      invoice_footer_note: null,
    },
    company_logo: null,
    whatsapp_group_id: null,
  });

  const [defaultFY, setDefaultFYState] = useState(getCurrentFY());
  const [defaultFYId, setDefaultFYIdState] = useState(null);

  const [allStores, setAllStores] = useState([]);
  const [currentStoreInfo, setCurrentStoreInfo] = useState(null);
  // The branch the session is in. null is a real value — it means "every
  // branch", which is what the three service roles get.
  const [currentBranch, setCurrentBranch] = useState(null);
  const [canSwitchBranch, setCanSwitchBranch] = useState(false);
  // Whether an "All branches" view is worth offering at all. The server
  // answers it (a Store Head or service role, of a store with more than one
  // branch) rather than leaving the client to work it out, because the server
  // has to enforce the same rule anyway and two implementations of one rule
  // eventually disagree.
  const [canViewAllBranches, setCanViewAllBranches] = useState(false);
  // Whether they are LOOKING at all of them right now.
  //
  // Purely a client-side view mode, and deliberately not part of the session:
  // /api/auth/switch-branch demands a real branch id, because the session is
  // always in exactly one place. This only widens what reports aggregate, and
  // it is what blocks creating anything — a new row has to record one branch
  // and there is no honest way to pick one while looking at every branch.
  //
  // Mirrored to sessionStorage rather than Preferences so it survives a reload
  // but not a fresh launch of the app. A view mode that outlives the sitting it
  // was chosen in turns up later as "why can't I raise a ticket", with nothing
  // on screen to connect it to a toggle flipped yesterday.
  const [viewingAllBranches, setViewingAllBranchesState] = useState(
    () => sessionStorage.getItem("viewing_all_branches") === "true",
  );

  // Turn the all-branches view on or off.
  //
  // Refuses to turn ON for anyone the server has not said may look at every
  // branch. A hidden control is not a permission and this is not a security
  // boundary — the report endpoint resolves the scope again from the role — but
  // a client that could put itself into a view the server will not serve would
  // sit there showing one branch's figures under an "All branches" heading,
  // which is worse than not offering it.
  const setViewingAllBranches = (next) => {
    const on = Boolean(next) && canViewAllBranches;
    setViewingAllBranchesState(on);
    if (on) {
      sessionStorage.setItem("viewing_all_branches", "true");
    } else {
      sessionStorage.removeItem("viewing_all_branches");
    }
  };
  // Ticket departments: what the ticket screens' department dropdown offers.
  const [allKumuduDepartments, setAllKumuduDepartments] = useState([]);
  const [allKumuduProjects, setAllKumuduProjects] = useState([]);
  const [allInternalDepartments, setAllInternalDepartments] = useState([]);
  const [allInternalProjects, setAllInternalProjects] = useState([]);
  // Org departments: the roots of the org chart / HR module. A separate list on
  // purpose — see src/utils/departmentTypes.js. A department marked as both
  // (`dept_org`) is in this list AND in the ticket list above, which is the
  // point: one row, visible on both sides.
  const [allKumuduOrgDepartments, setAllKumuduOrgDepartments] = useState([]);
  const [allInternalOrgDepartments, setAllInternalOrgDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [availableRoles, setAvailableRoles] = useState({});
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [reloadAppContext, setReloadAppContext] = useState(false);

  // Load stored login data
  useEffect(() => {
    const loadStoredAuth = async () => {
      const storedUser = await Preferences.get({ key: STORAGE_KEYS.USER });
      const storedRole = await Preferences.get({ key: STORAGE_KEYS.ROLE });
      const storedGoals = await Preferences.get({ key: STORAGE_KEYS.GOALS });
      const storedTemplates = await Preferences.get({
        key: STORAGE_KEYS.TEMPLATES,
      });
      const storedCompanySettings = await Preferences.get({
        key: STORAGE_KEYS.COMPANY_SETTINGS,
      });
      const storedFY = await Preferences.get({ key: STORAGE_KEYS.DEFAULT_FY });
      const storedFYId = await Preferences.get({
        key: STORAGE_KEYS.DEFAULT_FY_ID,
      });
      const storedStoreInfo = await Preferences.get({
        key: "current_store_info",
      });

      // Preferences is the durable copy; localStorage is the one the hiring
      // module's axios client reads. Re-mirror on boot so a session restored
      // from Preferences can still authenticate, and so a cleared session can
      // never leave a stale token behind for the next person on the device.
      const storedToken = await Preferences.get({ key: STORAGE_KEYS.TOKEN });
      if (storedToken.value) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, storedToken.value);
      } else {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
      }

      if (storedStoreInfo.value) {
        setCurrentStoreInfo(JSON.parse(storedStoreInfo.value));
      }

      const storedBranch = await Preferences.get({
        key: STORAGE_KEYS.CURRENT_BRANCH,
      });
      if (storedBranch.value) {
        try {
          setCurrentBranch(JSON.parse(storedBranch.value));
        } catch {
          setCurrentBranch(null);
        }
      }

      const storedCanSwitch = await Preferences.get({
        key: "can_switch_branch",
      });
      setCanSwitchBranch(storedCanSwitch.value === "true");

      const storedCanViewAll = await Preferences.get({
        key: "can_view_all_branches",
      });
      setCanViewAllBranches(storedCanViewAll.value === "true");
      if (storedUser.value) setUserInfo(JSON.parse(storedUser.value));
      if (storedRole.value) setRole(storedRole.value);
      if (storedGoals.value) setUserGoals(JSON.parse(storedGoals.value));
      if (storedTemplates.value)
        setTemplateData(JSON.parse(storedTemplates.value));
      if (storedCompanySettings.value)
        setCompanySettings(JSON.parse(storedCompanySettings.value));
      if (storedFY.value) {
        setDefaultFYState(parseInt(storedFY.value, 10));
      }
      if (storedFYId.value) {
        setDefaultFYIdState(parseInt(storedFYId.value, 10));
      }

      setLoadingUserData(false);
    };

    loadStoredAuth();
  }, []);

  const setDefaultFY = async (fyYear, fyId = null) => {
    const year = parseInt(fyYear, 10);
    setDefaultFYState(year);
    await Preferences.set({
      key: STORAGE_KEYS.DEFAULT_FY,
      value: String(year),
    });

    if (fyId !== null && fyId !== undefined) {
      const id = parseInt(fyId, 10);
      setDefaultFYIdState(id);
      await Preferences.set({
        key: STORAGE_KEYS.DEFAULT_FY_ID,
        value: String(id),
      });
    }
  };

  const saveLoginData = async ({
    user,
    role,
    tasks,
    template_token,
    templates,
    company_settings,
    fy,
    // The full store row for the session, when the caller already has it — both
    // OTP login and store switching get it back with the session payload.
    //
    // Caching it here instead of leaving it to the fetchStoreInfo effect below
    // matters for more than the saved round trip: it puts is_pawn / is_travel in
    // storage and in state in the SAME commit as the user, and those flags are
    // what decides the route a fresh login lands on (see getLoginLanding). Fetch
    // it a tick later and the app has already redirected to the wrong module.
    store,
    // Signed session token from the backend's buildSessionPayload. Modules
    // that authenticate per request read this instead of trusting a store_id
    // in the request body; the hiring module's tenant middleware is the one
    // that does so today.
    token,
    // The branch the server resolved for this session, and whether this
    // person may move between branches of the store they are in. Both come
    // straight from the session payload — neither is decided here, because
    // the server has to enforce the same answer anyway.
    current_branch,
    can_switch_branch,
    can_view_all_branches,
  }) => {
    const normalizedTasks = Array.isArray(tasks) ? tasks : [];
    const normalizedTemplates = Array.isArray(templates) ? templates : [];

    const templatePayload = {
      token: template_token || null,
      templates: normalizedTemplates,
    };

    const normalizedCompanySettings = {
      bank_details: {
        bank_name: company_settings?.bank_details?.bank_name || null,
        account_holder_name:
          company_settings?.bank_details?.account_holder_name || null,
        account_number: company_settings?.bank_details?.account_number || null,
        ifsc_code: company_settings?.bank_details?.ifsc_code || null,
        branch_name: company_settings?.bank_details?.branch_name || null,
        upi_id: company_settings?.bank_details?.upi_id || null,
      },
      invoice: {
        invoice_prefix: company_settings?.invoice?.invoice_prefix || null,
        invoice_footer_note:
          company_settings?.invoice?.invoice_footer_note || null,
      },
      company_logo: company_settings?.company_logo || null,
      whatsapp_group_id: company_settings?.whatsapp_group_id || null,
    };

    // Two homes on purpose. Preferences is the durable store the app boots
    // from; localStorage is what atsModule/services/api.js reads directly, and
    // on web Capacitor namespaces its own keys ("CapacitorStorage.token"), so
    // a Preferences-only write would be invisible to that interceptor.
    //
    // Absent token clears rather than keeps: this runs on store switch too, and
    // a retained token would still name the store we just left.
    if (token) {
      await Preferences.set({ key: STORAGE_KEYS.TOKEN, value: token });
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    } else {
      await Preferences.remove({ key: STORAGE_KEYS.TOKEN });
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
    }

    await Preferences.set({
      key: STORAGE_KEYS.USER,
      value: JSON.stringify(user),
    });
    await Preferences.set({ key: STORAGE_KEYS.ROLE, value: role });
    await Preferences.set({
      key: STORAGE_KEYS.GOALS,
      value: JSON.stringify(normalizedTasks),
    });
    await Preferences.set({
      key: STORAGE_KEYS.TEMPLATES,
      value: JSON.stringify(templatePayload),
    });
    await Preferences.set({
      key: STORAGE_KEYS.COMPANY_SETTINGS,
      value: JSON.stringify(normalizedCompanySettings),
    });

    if (store) {
      await Preferences.set({
        key: "current_store_info",
        value: JSON.stringify(store),
      });
    }

    // The branch is written unconditionally, including when it is null. A
    // service user has no branch, and leaving the previous session's branch
    // in storage would show them a chip for a place they are not in.
    if (current_branch) {
      await Preferences.set({
        key: STORAGE_KEYS.CURRENT_BRANCH,
        value: JSON.stringify(current_branch),
      });
    } else {
      await Preferences.remove({ key: STORAGE_KEYS.CURRENT_BRANCH });
    }
    await Preferences.set({
      key: "can_switch_branch",
      value: can_switch_branch ? "true" : "false",
    });
    await Preferences.set({
      key: "can_view_all_branches",
      value: can_view_all_branches ? "true" : "false",
    });

    if (fy?.fy_year) {
      await setDefaultFY(fy.fy_year, fy.fy_id ?? null);
    }

    // Before setUserInfo: the store is what the routing decision reads, so it
    // must never be the later of the two.
    if (store) setCurrentStoreInfo(store);
    setCurrentBranch(current_branch ?? null);
    setCanSwitchBranch(Boolean(can_switch_branch));
    setCanViewAllBranches(Boolean(can_view_all_branches));
    // Every path through here is a new session in a new place — a login, a
    // store switch, a branch switch. An all-branches view carried over from
    // the store just left would be aggregating a store nobody is in, and would
    // silently keep Create disabled after a switch made precisely to enable it.
    setViewingAllBranches(false);
    setUserInfo(user);
    setRole(role);
    setUserGoals(normalizedTasks);
    setTemplateData(templatePayload);
    setCompanySettings(normalizedCompanySettings);
  };

  // Move the session to a connected store.
  //
  // Every store-scoped cache is rewritten from the switch-store response, then
  // the app is reloaded. The reload is deliberate: store_id is read from context
  // in some screens and straight out of storage in others, and several keep
  // fetched rows in component state, so a soft switch risks showing one store's
  // data under another store's name. Rebuilding from storage is the only way to
  // guarantee nothing from the previous store survives.
  //
  // `redirectTo` is the route the new session belongs on — the target store's
  // module home (see getStoreHome). It matters because the target need not host
  // the same module as the store we're leaving: reloading in place would strand
  // a Travel ERP or plain store inside the /pawn screens. Passing it turns the
  // reload into a navigation, so there is still exactly one page load. The pawn
  // routes hold the line themselves as well (PawnLayout's pawn-only guard), for
  // the history entries a switch cannot reach.
  const switchStore = async (storeId, { redirectTo } = {}) => {
    // Storage is the fallback because the pawn routes render without waiting on
    // this context to hydrate, so their switcher can be clicked while userInfo is
    // still null.
    let mobile = userInfo?.mobile;
    if (!mobile) {
      const stored = await Preferences.get({ key: STORAGE_KEYS.USER });
      mobile = stored.value ? JSON.parse(stored.value)?.mobile : null;
    }

    // The store being left, read the same way. A switch is only allowed inside
    // the store group ("family") this session is in, and the server has no
    // session of its own to work that out from, so it has to be told.
    let fromStoreId = currentStoreInfo?.id;
    if (fromStoreId === undefined || fromStoreId === null) {
      const stored = await Preferences.get({ key: "current_store_info" });
      fromStoreId = stored.value ? JSON.parse(stored.value)?.id : null;
    }

    if (!mobile) throw new Error("No logged-in user to switch stores for");
    if (storeId === undefined || storeId === null) {
      throw new Error("No store selected");
    }

    // No branch_id: entering a store lands in the branch the ACCOUNT belongs
    // to, pinned-or-main, the same rule a login follows. That is also the
    // branch the switcher's row named, so the label and the landing agree.
    const res = await axios.post(`${API_BASE_URL}/api/auth/switch-store`, {
      mobile,
      store_id: storeId,
      from_store_id: fromStoreId ?? null,
    });

    if (!res?.data?.success) {
      throw new Error(res?.data?.statusMessage || "Failed to switch store");
    }

    const data = res.data.data;

    // user / role / tasks / templates / company settings / FY for the new store.
    // switch-store returns the full store row too, so the "current store" cache
    // is rebuilt from it with no second round trip (and no getUserStoreInfo
    // check, which the target store may not satisfy).
    await saveLoginData({
      user: data.user,
      role: data.user?.role,
      tasks: data.tasks,
      templates: data.templates,
      template_token: data.template_token,
      company_settings: data.company_settings,
      fy: data.fy,
      store: data.store,
      // Re-issued for the store being switched into, so the hiring module
      // scopes to the new tenant rather than the old one.
      token: data.token,
      current_branch: data.current_branch ?? null,
      can_switch_branch: Boolean(data.can_switch_branch),
      can_view_all_branches: Boolean(data.can_view_all_branches),
    });

    // Drafts and cached rows that are store-scoped and would survive a reload.
    sessionStorage.removeItem("accountEntry_headerState");
    sessionStorage.removeItem("individualLedger_state");

    // Lets the screen we land on keep the switching overlay up until the new
    // store's data has loaded, instead of flashing its own loader. Only the pawn
    // dashboard consumes this, so it is set only when that is where we land —
    // otherwise the flag would sit in storage and raise a stale overlay the next
    // time the pawn dashboard opened.
    const landing = redirectTo || null;
    const landsInPawn = landing
      ? landing.startsWith("/pawn")
      : window.location.pathname.startsWith("/pawn");

    if (data.store?.name && landsInPawn) {
      sessionStorage.setItem("pawnStoreSwitching", data.store.name);
    } else {
      sessionStorage.removeItem("pawnStoreSwitching");
    }

    // `replace`, not `assign`: the page we are leaving belongs to the OLD store,
    // so it must not be left behind as a back-button target.
    if (landing && landing !== window.location.pathname) {
      window.location.replace(landing);
    } else {
      window.location.reload();
    }

    return data;
  };

  // Move the session to another branch of the store it is already in.
  //
  // Unlike switchStore this does NOT reload the page. A store switch has to,
  // because settings, financial year, tasks, templates and every cached row
  // belong to the store being left. A branch move keeps all of that — only the
  // branch changes — so the caches stay valid and a reload would be a second of
  // white screen for nothing.
  //
  // That will stop being true in Phase 3, when module data starts being
  // branch-scoped: at that point screens holding fetched rows will need to
  // refetch on a branch change, the same way they would on a store change.
  // Whoever does that work should revisit this comment rather than discover it.
  const switchBranch = async (branchId) => {
    let mobile = userInfo?.mobile;
    if (!mobile) {
      const stored = await Preferences.get({ key: STORAGE_KEYS.USER });
      mobile = stored.value ? JSON.parse(stored.value)?.mobile : null;
    }

    let storeId = currentStoreInfo?.id;
    if (storeId === undefined || storeId === null) {
      const stored = await Preferences.get({ key: "current_store_info" });
      storeId = stored.value ? JSON.parse(stored.value)?.id : null;
    }

    if (!mobile) throw new Error("No logged-in user to switch branches for");
    if (storeId === undefined || storeId === null) {
      throw new Error("No store to switch branches within");
    }
    if (branchId === undefined || branchId === null) {
      throw new Error("No branch selected");
    }

    const res = await axios.post(`${API_BASE_URL}/api/auth/switch-branch`, {
      mobile,
      store_id: storeId,
      branch_id: branchId,
    });

    if (!res?.data?.success) {
      throw new Error(res?.data?.statusMessage || "Failed to switch branch");
    }

    const data = res.data.data;

    await saveLoginData({
      user: data.user,
      role: data.user?.role,
      tasks: data.tasks,
      templates: data.templates,
      template_token: data.template_token,
      company_settings: data.company_settings,
      fy: data.fy,
      store: data.store,
      // Re-issued carrying the new branch, so anything that scopes off the
      // token follows the session rather than trailing a branch behind it.
      token: data.token,
      current_branch: data.current_branch ?? null,
      can_switch_branch: Boolean(data.can_switch_branch),
      can_view_all_branches: Boolean(data.can_view_all_branches),
    });

    return data;
  };

  const clearLoginData = async () => {
    // Clear all states
    setUserInfo(null);
    setRole(null);
    setUserGoals([]);
    setTemplateData({ token: null, templates: [] });
    setCompanySettings({
      bank_details: {
        bank_name: null,
        account_holder_name: null,
        account_number: null,
        ifsc_code: null,
        branch_name: null,
        upi_id: null,
      },
      invoice: {
        invoice_prefix: null,
        invoice_footer_note: null,
      },
      company_logo: null,
      whatsapp_group_id: null,
    });
    setDefaultFYState(getCurrentFY());
    setDefaultFYIdState(null);
    setCurrentStoreInfo(null);
    setCurrentBranch(null);
    setCanSwitchBranch(false);
    setAllStores([]);
    setAllKumuduDepartments([]);
    setAllKumuduProjects([]);
    setAllInternalDepartments([]);
    setAllInternalProjects([]);
    setAllUsers([]);
    setAllFolders([]);
    setAvailableRoles({});

    // Clear all Preferences
    await Preferences.remove({ key: STORAGE_KEYS.USER });
    await Preferences.remove({ key: STORAGE_KEYS.ROLE });
    await Preferences.remove({ key: STORAGE_KEYS.GOALS });
    await Preferences.remove({ key: STORAGE_KEYS.TEMPLATES });
    await Preferences.remove({ key: STORAGE_KEYS.COMPANY_SETTINGS });
    await Preferences.remove({ key: STORAGE_KEYS.DEFAULT_FY });
    await Preferences.remove({ key: STORAGE_KEYS.DEFAULT_FY_ID });
    await Preferences.remove({ key: "current_store_info" });
    await Preferences.remove({ key: STORAGE_KEYS.CURRENT_BRANCH });
    await Preferences.remove({ key: "can_switch_branch" });
    await Preferences.remove({ key: "can_view_all_branches" });
    // A leftover from the build that replayed the last-used branch at login.
    // Nothing writes it now; clearing it here retires the copies already on
    // people's devices.
    await Preferences.remove({ key: LEGACY_LAST_BRANCH_KEY });
    await Preferences.remove({ key: STORAGE_KEYS.TOKEN });
    localStorage.removeItem(STORAGE_KEYS.TOKEN);

    // Clear sessionStorage items
    sessionStorage.removeItem("travelSplashShown");
    sessionStorage.removeItem("viewing_all_branches");
  };

  // Fetch all departments
  useEffect(() => {
    const allDepartments_URL = `${API_BASE_URL}/api/auth/getdept?store_id=${userInfo?.store_id}`;
    const allStores_URL = `${API_BASE_URL}/api/auth/stores`;
    const allUsers_URL = `${API_BASE_URL}/api/auth/stores/${userInfo?.store_id}/users`;

    const deptFetcher = async () => {
      try {
        if (userInfo?.store_id === 0) {
          const stores_res = await axios.get(allStores_URL);
          if (!stores_res?.data?.success)
            throw new Error("Failed to fetch stores");
          const stores = stores_res?.data?.data.filter(
            (store) => store.store_id !== 0,
          );
          setAllStores(stores);
        }

        const depts_res = await axios.get(allDepartments_URL);

        // One request, four lists. The endpoint returns every root row and the
        // split happens here, by type, so a screen can never accidentally read
        // the other side's departments: `isTicketDept` and `isOrgDept` both
        // match `dept_org`, which is how a department shows on both sides
        // without being duplicated as two rows.
        const kumudu = depts_res?.data?.data?.KUMUDU || [];
        const requested = depts_res?.data?.data?.REQUESTED_STORE || [];

        setAllKumuduDepartments(kumudu.filter(isTicketDept));
        setAllKumuduProjects(kumudu.filter(isProject));
        setAllKumuduOrgDepartments(kumudu.filter(isOrgDept));
        setAllInternalDepartments(requested.filter(isTicketDept));
        setAllInternalProjects(requested.filter(isProject));
        setAllInternalOrgDepartments(requested.filter(isOrgDept));

        if (userInfo !== null && userInfo?.store_id !== undefined) {
          const users_res = await axios.get(allUsers_URL);
          setAllUsers(users_res?.data?.data);
        }

        // Which roles this person may hand out, keyed by role_master id.
        // Two branches used to build this map by hand — one for Kumudu's
        // staff and one for a store head — and they disagreed about Service
        // HOD. assignableRolesFor keeps that behaviour exactly; the point of
        // moving it is that the rule now has one home instead of being
        // spelled out here in role strings that no longer exist.
        //
        // Assigned rather than merged into prevState. The map is entirely a
        // function of who is logged in, so merging could only ever carry an
        // earlier user's entries forward within one page session — which is
        // rare today, since a store switch reloads the page, but it was never
        // something the merge was there to achieve.
        if (userInfo !== null) {
          setAvailableRoles(assignableRolesFor(userInfo?.role));
        }
      } catch (error) {
        console.error(error.message);
      }
    };

    if (userInfo) {
      deptFetcher();
    }
  }, [userInfo, reloadAppContext]);

  // Fetch current store information
  const fetchStoreInfo = async (store_id, user_id) => {
    try {
      if (store_id === undefined || store_id === null || !user_id) {
        console.warn(
          "⚠️ Cannot fetch store info: missing store_id or user_id",
          { store_id, user_id },
        );
        return;
      }

      const res = await axios.get(
        `${API_BASE_URL}/api/auth/stores/${store_id}/${user_id}`,
      );

      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Failed to fetch store info");
      }

      const store = res.data.data.store;

      setCurrentStoreInfo(store);

      // Persist to storage
      await Preferences.set({
        key: "current_store_info",
        value: JSON.stringify(store),
      });
    } catch (error) {
      console.error("❌ fetchStoreInfo error:", error.message);
      // Don't set to null immediately - keep existing if available
      if (!currentStoreInfo) {
        setCurrentStoreInfo(null);
      }
    }
  };

  useEffect(() => {
    const loadStoreInfo = async () => {
      // Only fetch if we have userInfo and it has both store_id and id
      if (userInfo && userInfo.store_id !== undefined && userInfo.id) {
        // Check if we already have this store info cached
        const storedStoreInfo = await Preferences.get({
          key: "current_store_info",
        });
        let shouldFetch = true;

        if (storedStoreInfo.value) {
          const parsedStore = JSON.parse(storedStoreInfo.value);
          // If we already have the same store info in cache, use it
          if (parsedStore && parsedStore.id === userInfo.store_id) {
            setCurrentStoreInfo(parsedStore);
            shouldFetch = false;
          }
        }

        if (shouldFetch) {
          await fetchStoreInfo(userInfo.store_id, userInfo.id);
        }
      }
    };

    loadStoreInfo();
  }, [userInfo]);

  // What every branch-aware screen actually asks for.
  //
  // The client mirror of the backend's describeScope(), in the same shape and
  // with the same two answers: which branch(es) am I looking at, and may I
  // create something right now. Assembled here so no screen has to combine
  // currentBranch with viewingAllBranches itself and get the "all" case wrong.
  //
  //   mode "all"     every branch of the store — reports aggregate, nothing
  //                  may be created
  //   mode "branch"  one branch, named by branchId — the normal case
  //
  // A SERVICE ROLE SITS IN "branch" WITH A NULL branchId, which reads oddly but
  // is right: they are in no branch, they have not asked to view all of them,
  // and a null branch_id is exactly what their calls should carry. The server
  // then does the interpreting — resolveWriteBranch stamps the store's main
  // branch, and the report endpoint reads a null from an unscoped role as every
  // branch. Reporting "all" here instead would take creation away from Kumudu's
  // own staff, who have always been able to raise tickets in any store.
  const branchScope = (() => {
    const all = viewingAllBranches && canViewAllBranches;
    return {
      mode: all ? ALL_BRANCHES : "branch",
      // What to put in a branch_id query parameter: the literal "all", or an
      // id. Never undefined, so a caller can spread it without a guard.
      branchId: all ? ALL_BRANCHES : (currentBranch?.id ?? null),
      branchName: all ? "All branches" : (currentBranch?.name ?? null),
      branchCode: all ? null : (currentBranch?.code ?? null),
      // No, while looking at all branches. Blocking is deliberate: defaulting
      // to the main branch instead would silently mis-attribute everything a
      // head creates while reviewing the whole store, and the branch-wise
      // report they are looking at would then be wrong in a way nobody can see.
      //
      // A null branchId is NOT a reason to block. A service role has no branch
      // of its own and never has had: its create calls carry no branch_id and
      // the server stamps the store's main branch, exactly as it does today.
      // Only an explicit all-branches VIEW takes creation away.
      canCreate: !all,
    };
  })();

  return (
    <AutoTaskAuthContext.Provider
      value={{
        userInfo,
        role,
        userGoals,
        templateData,
        companySettings,
        allStores,
        currentStoreInfo,
        currentBranch,
        canSwitchBranch,
        switchBranch,
        canViewAllBranches,
        viewingAllBranches,
        setViewingAllBranches,
        branchScope,
        allKumuduDepartments,
        allKumuduProjects,
        allInternalDepartments,
        allInternalProjects,
        allKumuduOrgDepartments,
        allInternalOrgDepartments,
        allUsers,
        allFolders,
        availableRoles,
        saveLoginData,
        switchStore,
        clearLoginData,
        loadingUserData,
        setReloadAppContext,
        isLoggedIn: !!userInfo,
        defaultFY,
        setDefaultFY,
        defaultFYId,
      }}
    >
      {children}
    </AutoTaskAuthContext.Provider>
  );
};
